import { el, clear } from "../components/dom.js";
import { gameLinksRow } from "../components/game-links.js";
import { progressField, priceNoteField, loanField, startAgainButton } from "../components/tracking-fields.js";
import { reviewField, tagsField } from "../components/review-tags.js";
import { tagSuggestions } from "../utils/filters.js";
import { inTheNews } from "../components/in-the-news.js";
import { peopleRow } from "../components/people-row.js";
import { offerUndoDelete } from "../components/undo.js";
import { historySection } from "../components/history-section.js";
import {
	ENGAGEMENT_STATUSES,
	OWNERSHIP_STATUSES,
	OWNERSHIP_FORMATS,
	TIMES_FINISHED,
	AWARDS,
	awardLabel,
	statusLabel,
} from "../components/status-badge.js";
import {
	getGameById,
	updateGame,
	deleteGame,
	setRating,
	setOwnedPlatforms,
	setFormats,
	setTimesFinished,
	setAwards,
	setNote,
	setGameOwnedEditions,
	NOTE_MAX_LENGTH,
} from "../db/games.js";
import {
	refreshGame as refreshGameCatalog,
	gameNeedsRefresh,
	gameRecentlyChecked,
} from "../db/catalog-refresh.js";
import { ownedEditionsSection } from "../components/editions-picker.js";
import { getGroupsContainingItem, listGroups } from "../db/groups.js";
import { groupsSection } from "../components/groups-section.js";
import { getGameEditions, getGameSeriesForGame } from "../api/igdb.js";
import { listGames } from "../db/games.js";
import { addSeriesGameToGroup } from "./games-series.js";
import { formatDate, formatReleaseDate, formatReleaseWhen, todayDate } from "../utils/dates.js";
import { navigate, navigateBack, rerenderRoute } from "../main.js";
import { openItemMenu } from "../components/item-menu.js";
import { overriddenFields } from "../utils/overrides.js";
import { statusSticker } from "../components/magazine/status.js";
import {
	magDetailHero,
	magSummary,
	magField,
	magSelectRow,
	magChips,
	magSingleChips,
	magRating,
	magNote,
	magDeleteFooter,
	magDateLine,
	magSeriesMore,
} from "../components/magazine/detail.js";

export async function renderGameDetail(root, id) {
	const game = await getGameById(id);
	if (!game) {
		root.append(
			el(
				"div.view",
				{},
				el("p", {}, "Game not found."),
				el(
					"button.btn",
					{ type: "button", onclick: () => navigateBack("/games") },
					"Back",
				),
			),
		);
		return;
	}

	const view = el("div.view", {});
	root.append(view);
	mount(view, game);

	// Keep the catalog side honest. Release dates move (delays, TBD → announced),
	// so any game that could still shift is re-checked against IGDB on open — at
	// most once a day per game; the daily pass at boot usually got there first.
	// Games added before franchise/collection/release_date existed backfill those
	// too. Silent: if anything changed, the view re-renders.
	const needsCatalog = game.franchise == null && game.collection == null;
	const needsReleaseDate = game.release_date == null;
	if (
		game.igdb_id &&
		(needsCatalog || needsReleaseDate || gameNeedsRefresh(game, todayDate())) &&
		!gameRecentlyChecked(game.igdb_id)
	) {
		refreshGameCatalog(game)
			.then((updated) => {
				if (updated) mount(view, updated);
			})
			.catch(() => {});
	}
}

async function mount(view, game) {
	// Re-mounting rebuilds the whole view. Capture scroll up-front, then do all the
	// async work WHILE THE OLD VIEW IS STILL ON SCREEN; only clear + append + restore
	// synchronously at the very end, so the browser never paints a cleared/top state.
	const scrollY = window.scrollY;

	const groups = await getGroupsContainingItem(game);

	// ---- feature hero ----
	const kickerBits = ["Videogame"];
	if (game.release_year) kickerBits.push(String(game.release_year));
	if ((game.genres ?? []).length > 0) kickerBits.push(game.genres.slice(0, 2).join(" · "));

	const figures = [{ value: game.rating != null ? game.rating : "—", label: "Rating" }];
	if (game.franchise) figures.push({ value: game.franchise, label: "Franchise" });
	else if (game.release_date) figures.push({ value: formatReleaseWhen(game.release_date, game.release_precision), label: "Released" });

	if (overriddenFields(game).length > 0) kickerBits.push("Edited");

	const hero = magDetailHero(game, {
		kicker: kickerBits.join(" · "),
		title: game.title,
		sticker: statusSticker(game.engagement_status),
		figures,
		tag: (game.awards ?? [])[0] ? awardLabel(game.awards[0]) : null,
		onBack: () => navigateBack("/games"),
		// "⋯": edit details / re-link / undo / reset (components/item-menu.js). A
		// re-link swaps the catalog underneath, so rebuild the whole route for that.
		onMenu: () =>
			openItemMenu({
				mediaType: "videogame",
				record: game,
				onChanged: (updated, { relinked } = {}) =>
					relinked ? rerenderRoute() : mount(view, updated),
			}),
	});

	// ---- fields ----
	const ownsIt =
		game.ownership_status === "have_it" || game.ownership_status === "had_it";

	const engagementField = magField(
		"Played it?",
		magSelectRow(ENGAGEMENT_STATUSES, game.engagement_status, async (value) => {
			mount(view, await updateGame(game.id, { engagement_status: value }));
		}, (v) => statusLabel(v)),
		{ index: 0 },
	);

	const ownershipField = magField(
		"Got it?",
		magSelectRow(OWNERSHIP_STATUSES, game.ownership_status ?? "have_it", async (value) => {
			mount(view, await updateGame(game.id, { ownership_status: value }));
		}, (v) => statusLabel(v)),
		{ index: 1 },
	);

	const selectedFormats = new Set(game.formats ?? []);
	const formatField = ownsIt && magField(
		"Format",
		magChips(OWNERSHIP_FORMATS, selectedFormats, async (next) => {
			mount(view, await setFormats(game.id, Array.from(next)));
		}, (v) => statusLabel(v)),
		{ index: 2 },
	);

	const platformsAvailable = game.platforms ?? [];
	const ownedOn = new Set(game.owned_on ?? []);
	const platformsField = ownsIt && platformsAvailable.length > 0 && magField(
		"Platforms",
		magChips(platformsAvailable, ownedOn, async (next) => {
			mount(view, await setOwnedPlatforms(game.id, Array.from(next)));
		}),
		{ index: 1 },
	);

	const editionsField = ownsIt && magField(
		"Editions you own",
		ownedEditionsSection({
			owned: game.owned_editions ?? [],
			loadPage: ({ offset }) => getGameEditions(game.igdb_id, { offset, limit: 50 }),
			describe: describeGameEdition,
			persist: (list) => setGameOwnedEditions(game.id, list),
		}),
		{ index: 2 },
	);

	const ratingField = magField(
		"Rating",
		magRating(game.rating ?? null, async (value) => {
			mount(view, await setRating(game.id, value));
		}),
		{ index: 3 },
	);

	const selectedAwards = new Set(game.awards ?? []);
	const awardsField = magField(
		"Awards",
		magChips(AWARDS.map((a) => a.value), selectedAwards, async (next) => {
			mount(view, await setAwards(game.id, Array.from(next)));
		}, awardLabel),
		{ index: 4 },
	);

	const timesFinishedField = game.engagement_status === "done" && magField(
		"Times finished",
		magSingleChips(TIMES_FINISHED, game.times_finished ?? null, async (value) => {
			mount(view, await setTimesFinished(game.id, value));
		}),
		{ index: 2 },
	);

	const noteField = magField(
		"Notes",
		magNote(game.note ?? "", {
			maxLength: NOTE_MAX_LENGTH,
			placeholder: 'A quick note — e.g. "got stuck on the lava level"',
			onSave: (v) => setNote(game.id, v),
		}),
		{ index: 5 },
	);

	const groupsField = magField(
		"Groups",
		await groupsSection(game, groups, async () => mount(view, await refreshGame(game.id))),
		{ index: 1 },
	);

	const dates = magDateLine([
		["First completed", formatDate(game.date_first_completed)],
		["Last engaged", formatDate(game.last_engaged_date)],
		["Added", formatDate(game.created_at)],
	]);

	const footer = magDeleteFooter("Delete from library", async () => {
		if (!confirm(`Delete "${game.title}" from your list?`)) return;
		await deleteGame(game.id);
		navigate("/games");
		offerUndoDelete("videogame", game, { reopenRoute: `/games/${game.id}` });
	});

	// "More in this series" — filled async (an IGDB collection lookup) so the
	// detail renders immediately. IGDB games don't reliably store their collection,
	// so we always ask (best-effort) rather than gating on game.collection.
	const seriesMore = el("div", {});
	if (game.igdb_id) {
		(async () => {
			try {
				const series = await getGameSeriesForGame(game.igdb_id);
				const siblings = (series.games ?? []).filter((g) => g.igdb_id !== game.igdb_id);
				if (siblings.length === 0) return;
				const owned = new Set((await listGames()).map((g) => g.igdb_id));
				const entries = siblings.map((g) => ({
					title: g.name, cover_url: g.cover_url, year: g.release_year, owned: owned.has(g.igdb_id), raw: g,
				}));
				const meta = { collection_id: series.collection_id, title: series.title };
				clear(seriesMore);
				seriesMore.append(magSeriesMore("videogame", {
					entries,
					onAdd: async (e) => {
						await addSeriesGameToGroup(meta, e.raw);
						mount(view, await getGameById(game.id));
					},
					onAddAll: async () => {
						for (const g of series.games ?? []) await addSeriesGameToGroup(meta, g);
						const grp = (await listGroups()).find(
							(x) => x.media_type === "videogame" && (x.name ?? "").toLowerCase() === (series.title ?? "").toLowerCase(),
						);
						if (grp) navigate(`/groups/${grp.id}`);
						else mount(view, await getGameById(game.id));
					},
				}));
			} catch {
				// best-effort
			}
		})();
	}

	clear(view);
	view.append(
		hero,
		magSummary(game.summary) || "",
		inTheNews(game),
		peopleRow("game", game.igdb_id),
		gameLinksRow(game.igdb_id),
		engagementField,
		ownershipField,
		formatField || "",
		platformsField || "",
		editionsField || "",
		ratingField,
		awardsField,
		timesFinishedField || "",
		noteField,
		tagsField("videogame", game, { suggestions: tagSuggestions("videogame") }),
		reviewField("videogame", game),
		progressField("videogame", game),
		priceNoteField("videogame", game),
		loanField("videogame", game),
		startAgainButton("videogame", game),
		groupsField,
		seriesMore,
		dates,
		historySection(game),
		footer,
	);

	window.scrollTo(0, scrollY);
}

async function refreshGame(id) {
	return getGameById(id);
}

// Render a game edition (API result or stored snapshot — same fields) into the
// { title, sub, cover_url } shape the editions picker/chips use.
function describeGameEdition(e) {
	const title = e.label || "Edition";
	const sub =
		[e.release_year, (e.platforms ?? []).join(" / ")]
			.filter(Boolean)
			.join(" · ") || null;
	return { title, sub, cover_url: e.cover_url ?? null };
}
